// BluprintJs
import { Button, Card, ControlGroup, Intent } from "@blueprintjs/core"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomSpinner from "components/Atoms/CustomSpinner"
import CustomSwitch from "components/Atoms/CustomSwitch"
import { cloneDeep, get, set } from "lodash-es"
import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
// Form Components
import StaticMultiSelector from "components/Atoms/StaticMultiSelector"
import StringInput from "components/Atoms/StringInput"
import Error from "components/Common/Error"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import MutationOptions from "components/Organisms/MutationOptions"
import DimensionsInput from "components/Templates/form/DimensionsInput"
import { BELONGS_TO } from "data/static.items"
import { useSkuDetailsLazyQuery } from "generated/graphql"
import { SI_UNITS, SKU_CATEGORIES } from "graphql/selector.queries.gql"
import { ADD_SKU, SKUS_LIST } from "graphql/sku.gql"
import { useFormDisabled } from "hooks/useAuth"
import { getErrors } from "hooks/useErrors"
import skuModel from "models/atoms/sku.model"
import AppToaster from "utils/AppToaster"

const INITIAL_STATE = {
  metadata: {
    labels: BELONGS_TO.filter(b => b.id === "vesatogo"),
  },
}
export default function SkuRegistration() {
  const { id } = useParams()
  const idNumber = parseInt(id)
  const navigate = useNavigate()
  const [sku, setSku] = useState<any>(INITIAL_STATE)
  const [getSkuDetails, { error, loading }] = useSkuDetailsLazyQuery({
    onCompleted({ skuDetails }) {
      const metadata = JSON.parse(skuDetails?.metadata || "{}")
      setSku({
        ...skuDetails,
        metadata: {
          ...metadata,
          labels:
            metadata.labels?.map(l => ({
              id: l,
              name: l,
            })) || BELONGS_TO.filter(b => b.id === "vesatogo"),
        },
      })
    },
  })
  useEffect(() => {
    if (idNumber) {
      getSkuDetails({ variables: { id: idNumber } })
    } else {
      setSku(INITIAL_STATE)
    }
    return function cleanup() {
      setSku(INITIAL_STATE)
    }
  }, [idNumber, getSkuDetails])

  function handleChange(path, value) {
    const skuDetailsCopy = cloneDeep(sku)
    set(skuDetailsCopy, path, value)
    setSku(skuDetailsCopy)
  }
  function showToast({ addUpdateSkuType }) {
    const sku = get(addUpdateSkuType, "skuType")
    AppToaster.show({
      message: `SKU ${sku.name} ${sku.id ? "Edited" : "Saved"} successfully.`,
      intent: Intent.SUCCESS,
    })
  }

  const parsedVariables = useMemo(
    () => ({
      ...sku,
      skuCategory: sku?.skuCategory?.id,
      capacityUnit: sku?.capacityUnit?.id,
      dimensionUnit: sku?.dimensionUnit?.id,
      metadata: JSON.stringify({
        ...sku.metadata,
        labels: sku.metadata?.labels?.map(l => l.id),
      }),
    }),
    [sku]
  )

  const errors = useMemo(() => {
    return getErrors({ schema: skuModel, state: sku })
  }, [sku])

  const disabled = useFormDisabled("sku", parsedVariables)
  if (loading) return <CustomSpinner />
  if (error) return <Error message={error.message} />
  return (
    <Card>
      <div className="mt-2 w-full flex justify-between">
        <span>
          <Button
            icon="arrow-left"
            onClick={() => {
              navigate(-1)
            }}
            minimal={true}
          ></Button>
          <h5 className="ml-3" style={{ display: "inline-block" }}>
            {id ? "Edit" : "New"} SKU
          </h5>
        </span>
      </div>

      <div className="container mt-3">
        <FormController
          source={sku}
          errors={errors.details}
          onChange={handleChange}
          disabled={disabled}
          disableAll={true}
        >
          <div className="form-grid">
            <StringInput
              autoFocus={true}
              label="SKU Name"
              helperText="eg. Box"
              path="name"
            ></StringInput>
            <DynamicSelector
              big={true}
              query={SKU_CATEGORIES}
              path="skuCategory"
              label="Category"
            />
            <ControlGroup>
              <StringInput
                label="Capacity"
                helperText="eg. 100 Kg"
                path="capacity"
              ></StringInput>
              <DynamicSelector
                big={true}
                fill={false}
                label="(Unit.)"
                query={SI_UNITS}
                path="capacityUnit"
              />
            </ControlGroup>
            <StringInput
              label="Description"
              helperText="eg. Wooden"
              path="description"
            ></StringInput>
            <StringInput
              label="Tare Weight"
              path="selfWeight"
              helperText="eg. 34.98 (Kg)"
            ></StringInput>
            <StringInput
              label="Stock Count"
              path="stockCount"
              helperText="eg. 100"
            ></StringInput>
            <DimensionsInput />
            <StringInput
              leftIcon="rupee"
              path="unitPrice"
              type="number"
              label="Unit Price"
            />
            <StaticMultiSelector
              fill
              creatable
              big={true}
              onCreate={() => {}}
              items={BELONGS_TO}
              path="metadata.labels"
              label="Labels"
            />
            <CustomFormGroup label="Returnable" big={true}>
              <CustomSwitch large={true} path="isReturnable" />
            </CustomFormGroup>
          </div>
        </FormController>

        <div className="flex justify-end">
          <MutationOptions
            disabled={disabled}
            mutation={ADD_SKU}
            variables={parsedVariables}
            onError={error => {
              AppToaster.show({
                message: "Unable to save sku",
                intent: Intent.DANGER,
              })
            }}
            refetchQueries={[
              {
                query: SKUS_LIST,
                variables: {
                  first: 13,
                  skip: 0,
                },
              },
            ]}
            onSave={data => {
              setSku(INITIAL_STATE)
              showToast(data)
              navigate(-1)
            }}
            onSaveAndAdd={data => {
              showToast(data)
              setSku(INITIAL_STATE)
            }}
            onSaveAndEdit={data => {
              const skuId = data?.addUpdateSkuType?.skuType?.id
              showToast(data)
              if (!parseInt(id))
                navigate(`/d/registration/sku/${skuId}`, { replace: true })
            }}
          ></MutationOptions>
        </div>
      </div>
    </Card>
  )
}
